
import React, { useEffect, useMemo, useState } from 'react';
import '@fontsource/montserrat/700.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/400.css';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, AreaChart, Area,
} from 'recharts';
import axiosInstance from '../utils/axios';
import {
  Box, Grid, Card, CardContent, Typography, Stack, Avatar, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Pagination, IconButton, Container, Fade, LinearProgress, TextField, alpha, Alert,
  Tabs, Tab, Chip, Divider,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AccountBalanceWallet from '@mui/icons-material/AccountBalanceWallet';
import Assignment from '@mui/icons-material/Assignment';
import Timeline from '@mui/icons-material/Timeline';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import SportsEsportsOutlinedIcon from '@mui/icons-material/SportsEsportsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import MilitaryTechOutlinedIcon from '@mui/icons-material/MilitaryTechOutlined';
import { motion } from 'framer-motion';
import { myContracts } from '../services/contractsService';
import { getMyWallet } from '../services/walletService';
import WalletTransactions from '../components/WalletTransactions';
import ActiveContracts from '../components/ActiveContracts';
import CompletedContracts from '../components/CompletedContracts';
import LicenseCard from '../components/LicenseCard';
import MagicPageShell from '../components/magicui/MagicPageShell';
import { BentoGrid, BentoItem } from '../components/magicui/BentoGrid';
import NumberTicker from '../components/magicui/NumberTicker';
import AnimatedTabPanel from '../components/magicui/AnimatedTabPanel';
import AnimatedGridPattern from '../components/magicui/AnimatedGridPattern';
import RevealSection from '../components/magicui/RevealSection';
import RiderOnboardingChecklist from '../components/RiderOnboardingChecklist';
import ConnectedAccountBadges from '../components/ConnectedAccountBadges';
import DailyLoginBonusBanner from '../components/DailyLoginBonusBanner';

const font = "'Montserrat', sans-serif";

const T = {
  bg: '#09090B',
  surface: '#111113',
  surfaceAlt: '#0F0F11',
  surfaceHover: '#1A1A1D',
  surfaceElevated: '#161618',
  border: 'rgba(255,255,255,0.06)',
  borderHover: 'rgba(255,255,255,0.1)',
  borderStrong: 'rgba(255,255,255,0.14)',
  text: '#FAFAFA',
  textSecondary: '#A1A1AA',
  textMuted: '#71717A',
  textFaint: '#3F3F46',
  accent: '#E4FF1A',
  accentDim: 'rgba(228,255,26,0.06)',
  accentMid: 'rgba(228,255,26,0.12)',
  success: '#34D399',
  successDim: 'rgba(52,211,153,0.08)',
  info: '#60A5FA',
  infoDim: 'rgba(96,165,250,0.08)',
  warning: '#FBBF24',
  warningDim: 'rgba(251,191,36,0.08)',
  danger: '#FB7185',
  dangerDim: 'rgba(251,113,133,0.08)',
  radius: '10px',
  radiusSm: '6px',
  radiusXs: '4px',
};

const sxCard = {
  bgcolor: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: T.radius,
  boxShadow: 'none',
  transition: 'border-color 0.2s ease, transform 0.2s ease',
  '&:hover': { borderColor: T.borderHover },
};

const sxLabel = {
  fontFamily: font,
  fontSize: '0.8rem',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: T.textMuted,
};

const sxValue = {
  fontFamily: font,
  fontSize: '1.85rem',
  fontWeight: 700,
  color: T.text,
  lineHeight: 1.15,
  letterSpacing: '-0.02em',
};

const DASH_TABS = [
  { id: 'overview', label: 'Overview', icon: <BoltOutlinedIcon sx={{ fontSize: 16 }} /> },
  { id: 'drive', label: 'Drive', icon: <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} /> },
  { id: 'events', label: 'Events', icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} /> },
  { id: 'career', label: 'Career', icon: <MilitaryTechOutlinedIcon sx={{ fontSize: 16 }} /> },
  { id: 'profile', label: 'Profile', icon: <SportsEsportsOutlinedIcon sx={{ fontSize: 16 }} /> },
];

const QUICK_LINKS = [
  { label: 'Contract Hub', to: '/contracts/me', icon: <Assignment sx={{ fontSize: 18 }} />, blurb: 'Active hauls & payouts' },
  { label: 'Wallet', to: '/wallet', icon: <AccountBalanceWallet sx={{ fontSize: 18 }} />, blurb: 'Tokens & driver pass' },
  { label: 'My jobs', to: '/jobs', icon: <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />, blurb: 'Delivery history' },
  // { label: 'Validate job', to: '/validate-job', icon: <VerifiedOutlinedIcon sx={{ fontSize: 18 }} />, blurb: 'Submit a Job ID' },
  { label: 'Streak & rewards', to: '/attendance/me', icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />, blurb: 'Event streak bonuses' },
  { label: 'Leaderboard', to: '/leaderboard', icon: <Timeline sx={{ fontSize: 18 }} />, blurb: 'Company rankings' },
  { label: 'Calendar', to: '/calendar', icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />, blurb: 'Convoy slots' },
  { label: 'Challenges', to: '/challenges', icon: <FlagOutlinedIcon sx={{ fontSize: 18 }} />, blurb: 'Earn challenge XP' },
  { label: 'Results', to: '/results', icon: <SpeedOutlinedIcon sx={{ fontSize: 18 }} />, blurb: 'Stats snapshot' },
  { label: 'Fleet', to: '/fleet', icon: <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 18 }} />, blurb: 'Trucks & garage' },
  { label: 'Goals', to: '/goals', icon: <EmojiEventsOutlinedIcon sx={{ fontSize: 18 }} />, blurb: 'Personal targets' },
  { label: 'Division', to: '/division', icon: <GroupsOutlinedIcon sx={{ fontSize: 18 }} />, blurb: 'Fleet & tax board' },
];

function StatCard({ icon, label, value, accent, tint, ticker }) {
  const bg = tint || (accent ? T.accentDim : 'rgba(255,255,255,0.02)');
  const iconColor = accent ? T.accent : T.textSecondary;
  const numeric = typeof value === 'number';
  return (
    <Card sx={{ ...sxCard, height: '100%', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
        background: accent ? `linear-gradient(90deg, ${T.accent}, transparent)` : 'transparent',
      }} />
      <CardContent sx={{ p: '20px !important' }}>
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={sxLabel}>{label}</Typography>
            <Box sx={{
              width: 34, height: 34,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: bg, borderRadius: T.radiusSm, color: iconColor,
            }}>
              {icon}
            </Box>
          </Stack>
          <Typography sx={sxValue}>
            {ticker && numeric ? <NumberTicker value={value} /> : value}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SectionTitle({ children, action }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} spacing={1}>
      <Typography sx={{
        fontFamily: font,
        fontSize: '0.85rem',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: T.textMuted,
      }}>
        {children}
      </Typography>
      {action}
    </Stack>
  );
}

function InfoCell({ label, value, color }) {
  return (
    <Box sx={{
      p: '12px 14px',
      border: `1px solid ${T.border}`,
      borderRadius: T.radiusSm,
      bgcolor: T.surfaceAlt,
      transition: 'border-color 0.2s ease',
      '&:hover': { borderColor: T.borderHover },
    }}>
      <Typography sx={{ ...sxLabel, fontSize: '0.75rem', mb: 0.5 }}>{label}</Typography>
      <Typography sx={{
        fontFamily: font, fontSize: '1.2rem', fontWeight: 700,
        color: color || T.text, lineHeight: 1.2,
      }}>
        {value}
      </Typography>
    </Box>
  );
}

function TagChip({ label, color = T.textMuted, filled }) {
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center',
      px: '10px', py: '4px',
      border: `1px solid ${filled ? 'transparent' : T.border}`,
      borderRadius: T.radiusXs,
      bgcolor: filled ? alpha(color, 0.1) : 'transparent',
    }}>
      <Typography sx={{
        fontFamily: font, fontSize: '0.85rem', fontWeight: 500,
        color, letterSpacing: '0.01em',
      }}>
        {label}
      </Typography>
    </Box>
  );
}

function StatusBadge({ label, color = T.success }) {
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.75,
      px: '10px', py: '3px',
      border: `1px solid ${alpha(color, 0.3)}`,
      borderRadius: T.radiusXs,
      bgcolor: alpha(color, 0.06),
    }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: color }} />
      <Typography sx={{
        fontFamily: font, fontSize: '0.78rem', fontWeight: 700,
        color, letterSpacing: '0.04em', textTransform: 'uppercase',
      }}>
        {label}
      </Typography>
    </Box>
  );
}

function ActionTile({ label, blurb, icon, to, accent }) {
  return (
    <Button
      component={RouterLink}
      to={to}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1,
        p: '16px !important',
        height: '100%',
        minHeight: 108,
        textAlign: 'left',
        textTransform: 'none',
        borderRadius: T.radius,
        border: `1px solid ${accent ? alpha(T.accent, 0.35) : T.border}`,
        bgcolor: accent ? T.accentDim : T.surface,
        color: T.text,
        transition: 'border-color 0.2s ease, background 0.2s ease, transform 0.2s ease',
        '&:hover': {
          borderColor: accent ? alpha(T.accent, 0.55) : T.borderHover,
          bgcolor: accent ? alpha(T.accent, 0.1) : T.surfaceHover,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: T.radiusSm,
          display: 'grid', placeItems: 'center',
          bgcolor: accent ? alpha(T.accent, 0.16) : 'rgba(255,255,255,0.04)',
          color: accent ? T.accent : T.textSecondary,
        }}>
          {icon}
        </Box>
        <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: '0.92rem', flex: 1 }}>{label}</Typography>
        <ArrowForwardRoundedIcon sx={{ fontSize: 16, color: T.textMuted }} />
      </Stack>
      <Typography sx={{ fontFamily: font, fontSize: '0.78rem', color: T.textMuted, lineHeight: 1.35 }}>
        {blurb}
      </Typography>
    </Button>
  );
}

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      bgcolor: T.surfaceElevated, border: `1px solid ${T.borderStrong}`,
      borderRadius: T.radiusSm, p: '12px 16px', backdropFilter: 'blur(12px)',
    }}>
      <Typography sx={{ ...sxLabel, mb: 1, fontSize: '0.75rem' }}>{label}</Typography>
      {payload.map((p, i) => (
        <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: p.color }} />
          <Typography sx={{ fontFamily: font, fontSize: '0.85rem', color: T.textMuted }}>
            {p.name}:
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: '0.85rem', color: T.text, fontWeight: 600 }}>
            {Number(p.value).toLocaleString()}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
};

function cityLabel(v) {
  if (!v) return '—';
  if (typeof v === 'string') return v;
  // Job.source / destination: { city: { id, name }, ... }
  const city = v.city;
  if (typeof city === 'string') return city;
  if (city && typeof city === 'object') return city.name || city.id || '—';
  if (typeof v.name === 'string') return v.name;
  return '—';
}

function jobRevenue(j) {
  return Number(j?.normalized?.revenueValue ?? j?.revenue ?? j?.income ?? 0);
}

export default function UserDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [dashTab, setDashTab] = useState(() => searchParams.get('tab') || 'overview');
  const [linkBanner, setLinkBanner] = useState(null);
  const [linkBusy, setLinkBusy] = useState('');
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [attendancePage, setAttendancePage] = useState(1);
  const pageSize = 10;
  const [jobid, Setjobid] = useState('');
  const [activeAttendanceEvents, setActiveAttendanceEvents] = useState([]);
  const [attendanceSubmitLoading, setAttendanceSubmitLoading] = useState(false);
  const [attendanceSubmitMsg, setAttendanceSubmitMsg] = useState('');
  const [jobmessage, setjobmessage] = useState('');
  const [contracts, setContracts] = useState({ active: [], history: [] });
  const [wallet, setWallet] = useState({ balance: 0, transactions: [] });
  const [externalUpcoming, setExternalUpcoming] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  const [pendingDivisionInvites, setPendingDivisionInvites] = useState(0);
  const [divisionSummary, setDivisionSummary] = useState(null);
  const [currentDivision, setCurrentDivision] = useState(null);
  const [latestJobs, setLatestJobs] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editForm, setEditForm] = useState({
    name: '',
    tmpIngameName: '',
    avatar: '',
    age: '',
    country: '',
    steamID: '',
    truckersmpId: '',
    truckershubId: '',
    trucklineMpId: '',
  });
  const [loginBonusDialogOpen, setLoginBonusDialogOpen] = useState(false);

  const reloadDashboard = async () => {
    const { data: dash } = await axiosInstance.get('/me/dashboard');
    setData(dash);
    return dash;
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: dash } = await axiosInstance.get('/me/dashboard');
        setData(dash);
        const dailyAwarded = Boolean(dash?.dailyLoginBonus?.awarded && dash.dailyLoginBonus.amount > 0);
        const chestAwarded = Boolean(dash?.weeklyLoginChest?.awarded && dash.weeklyLoginChest.amount > 0);
        if (dailyAwarded || chestAwarded) {
          setLoginBonusDialogOpen(true);
          // Refresh wallet after credits applied on dashboard load
          try {
            const w = await getMyWallet();
            setWallet({
              balance: Number(w.balance || 0),
              transactions: Array.isArray(w.transactions) ? w.transactions : [],
            });
          } catch (_) { /* ignore */ }
        }
      } catch {
        setError('Failed to load dashboard');
      }
    })();
  }, []);

  // Handle OAuth link redirects (?steam_link= / ?tlmp_link=) and deep-link tab
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setDashTab(tab);

    const steamLink = searchParams.get('steam_link');
    const tlmpLink = searchParams.get('tlmp_link');
    if (!steamLink && !tlmpLink) return;

    const messages = {
      steam_success: { severity: 'success', text: 'Steam account linked successfully.' },
      steam_taken: { severity: 'error', text: 'That Steam account is already linked to another rider.' },
      steam_expired: { severity: 'warning', text: 'Steam link session expired. Please try again.' },
      steam_error: { severity: 'error', text: 'Steam linking failed. Please try again.' },
      tlmp_success: { severity: 'success', text: 'Truckline MP account linked successfully.' },
      tlmp_taken: { severity: 'error', text: 'That Truckline MP account is already linked to another rider.' },
      tlmp_expired: { severity: 'warning', text: 'Truckline MP link session expired. Please try again.' },
      tlmp_denied: { severity: 'warning', text: 'Truckline MP authorization was cancelled.' },
      tlmp_no_rider: { severity: 'error', text: 'No rider profile found to link Truckline MP.' },
      tlmp_error: { severity: 'error', text: 'Truckline MP linking failed. Please try again.' },
    };

    if (steamLink) setLinkBanner(messages[`steam_${steamLink}`] || messages.steam_error);
    if (tlmpLink) setLinkBanner(messages[`tlmp_${tlmpLink}`] || messages.tlmp_error);

    if (steamLink === 'success' || tlmpLink === 'success') {
      reloadDashboard().catch(() => {});
    }

    const next = new URLSearchParams(searchParams);
    next.delete('steam_link');
    next.delete('tlmp_link');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await myContracts();
        setContracts(res);
      } catch (e) { console.log(e); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const w = await getMyWallet();
        setWallet({
          balance: Number(w.balance || 0),
          transactions: Array.isArray(w.transactions) ? w.transactions : [],
        });
      } catch (e) { console.log(e); }
    })();
  }, [data?.wallet?.balance]);

  useEffect(() => {
    (async () => {
      try {
        const { data: jobs } = await axiosInstance.get('/me/jobs', { params: { limit: 40, sort: '-createdAt' } });
        setLatestJobs(Array.isArray(jobs?.items) ? jobs.items : []);
      } catch {
        setLatestJobs([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data: ev } = await axiosInstance.get('/attendance-events/active/me');
        setActiveAttendanceEvents(Array.isArray(ev) ? ev : []);
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data: inv } = await axiosInstance.get('/me/division/invites');
        setPendingDivisionInvites((inv?.invites || []).length);
      } catch {
        setPendingDivisionInvites(0);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data: sum } = await axiosInstance.get('/me/division/summary');
        setDivisionSummary(sum?.division ? sum : null);
      } catch {
        setDivisionSummary(null);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data: div } = await axiosInstance.get('/me/division');
        setCurrentDivision(div?.division || null);
      } catch {
        setCurrentDivision(null);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data: list } = await axiosInstance.get('/calendar/today-events');
        const rows = Array.isArray(list) ? list : [];
        rows.sort((a, b) => new Date(a.meetupTime || 0).getTime() - new Date(b.meetupTime || 0).getTime());
        setTodayEvents(rows);
      } catch {
        setTodayEvents([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data: feed } = await axiosInstance.get('/calendar/feed');
        const list = Array.isArray(feed) ? feed : [];
        const now = Date.now();
        const upcoming = list
          .map((ev) => ({
            id: ev._id,
            title: ev.eventTitle || ev.title,
            start: new Date(ev.Meetuptime || ev.start).getTime(),
            link: ev.eventLink,
          }))
          .filter((ev) => ev.start >= now)
          .sort((a, b) => a.start - b.start)
          .slice(0, 6);
        setExternalUpcoming(upcoming);
      } catch { setExternalUpcoming([]); }
    })();
  }, []);

  const handleJobsubmit = async (jobidParam) => {
    try {
      setjobmessage('');
      const id = (jobidParam ?? jobid ?? '').toString().trim();
      if (!id) {
        setjobmessage('Please enter a JobId');
        setTimeout(() => setjobmessage(''), 3000);
        return;
      }
      const resp = await axiosInstance.post('/hook/manual-jobs', { jobIDs: [id] });
      if (resp.status === 200 || resp.status === 201) {
        setjobmessage('Submitted for validation');
        Setjobid('');
      } else setjobmessage(resp.data?.message || 'Failed to submit');
    } catch (e) {
      setjobmessage(e?.response?.data?.message || 'Failed to submit');
    } finally {
      setTimeout(() => setjobmessage(''), 3000);
    }
  };

  const handleMarkAttendance = async (eventId) => {
    try {
      setAttendanceSubmitLoading(true);
      setAttendanceSubmitMsg('');
      await axiosInstance.post(`/attendance-events/${eventId}/mark-attendance`, {});
      setAttendanceSubmitMsg('Attendance submitted');
      const { data: ev } = await axiosInstance.get('/attendance-events/active/me');
      setActiveAttendanceEvents(Array.isArray(ev) ? ev : []);
    } catch (e) {
      setAttendanceSubmitMsg(e?.response?.data?.message || 'Failed');
    } finally {
      setAttendanceSubmitLoading(false);
      setTimeout(() => setAttendanceSubmitMsg(''), 3000);
    }
  };

  const chartPack = useMemo(() => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date();
    const monthsWindow = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      return { key: `${d.getFullYear()}-${d.getMonth()}`, label: monthNames[d.getMonth()] };
    });
    const monthlyMap = new Map(monthsWindow.map((m) => [m.key, { revenue: 0, distance: 0, label: m.label }]));
    latestJobs.forEach((j) => {
      const ts = j.deliveredTimestamp || j.createdAt;
      if (!ts) return;
      const d = new Date(ts);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (monthlyMap.has(key)) {
        const cur = monthlyMap.get(key);
        cur.revenue += jobRevenue(j);
        cur.distance += Number(j.distanceDriven || 0);
      }
    });
    const revenueData = Array.from(monthlyMap.values()).map((m) => ({
      month: m.label,
      revenue: Math.round(m.revenue),
      distance: Math.round(m.distance),
    }));

    const weekdayOrder = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const weeklyAgg = new Map(weekdayOrder.map((d) => [d, { jobs: 0, distance: 0 }]));
    latestJobs.forEach((j) => {
      const ts = j.deliveredTimestamp || j.createdAt;
      if (!ts) return;
      const d = new Date(ts);
      const label = weekdayOrder[d.getDay()];
      const cur = weeklyAgg.get(label) || { jobs: 0, distance: 0 };
      cur.jobs += 1;
      cur.distance += Number(j.distanceDriven || 0);
      weeklyAgg.set(label, cur);
    });
    const weeklyData = weekdayOrder.map((day) => ({
      day,
      jobs: (weeklyAgg.get(day) || {}).jobs || 0,
    }));

    return { revenueData, weeklyData, hasData: latestJobs.length > 0 };
  }, [latestJobs]);

  if (error) {
    return (
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" sx={{ bgcolor: T.bg }}>
        <Stack spacing={2} alignItems="center">
          <Typography sx={{ fontFamily: font, color: T.danger, fontSize: '1.05rem', fontWeight: 500 }}>{error}</Typography>
          <Button
            onClick={() => window.location.reload()}
            sx={{
              fontFamily: font, fontSize: '0.88rem', color: T.text, border: `1px solid ${T.border}`,
              borderRadius: T.radiusSm, textTransform: 'none', px: 3,
              '&:hover': { borderColor: T.borderHover, bgcolor: T.surfaceHover },
            }}
          >
            Retry
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" sx={{ bgcolor: T.bg }}>
        <Stack spacing={2} alignItems="center">
          <LinearProgress sx={{
            width: 140, height: 2, bgcolor: T.border, borderRadius: 1,
            '& .MuiLinearProgress-bar': { bgcolor: T.accent, borderRadius: 1 },
          }} />
          <Typography sx={{ ...sxLabel, fontSize: '0.75rem' }}>Loading dashboard</Typography>
        </Stack>
      </Box>
    );
  }

  const {
    user,
    rider,
    progress = [],
    completions = [],
    totals = { totalKm: 0, totalRevenue: 0, totalJobs: 0 },
    attendance = { totalEventsAttended: 0, eventsAttended: [] },
    achievements = [],
    truckershubStats,
  } = data;

  const thSnapshot = truckershubStats?.snapshot || {};
  const thTotals = {
    totalJobs: Number(thSnapshot.jobs ?? totals.totalJobs ?? 0),
    totalDistance: Number(thSnapshot.statistics?.distance?.total ?? thSnapshot.distance ?? totals.totalKm ?? 0),
    totalRevenue: Number(thSnapshot.statistics?.income?.total ?? thSnapshot.income ?? totals.totalRevenue ?? 0),
    avgPerJob: (() => {
      const jobs = Number(thSnapshot.jobs ?? totals.totalJobs ?? 0);
      const rev = Number(thSnapshot.statistics?.income?.total ?? thSnapshot.income ?? totals.totalRevenue ?? 0);
      return jobs > 0 ? Math.round((rev / jobs) * 100) / 100 : totals.avgPerJob || 0;
    })(),
  };

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  const displayName = rider?.name || user?.username || 'Driver';
  const firstName = displayName.split(' ')[0];
  const activeDivision = divisionSummary?.division || currentDivision;
  const driverPass = data?.driverPass || null;
  const passStatusLabel = driverPass?.valid ? 'Valid pass' : 'No valid pass';
  const passStatusColor = driverPass?.valid ? T.success : T.danger;
  const steamIdResolved = rider?.steamID64 || rider?.steamID || user?.steamId || null;
  const canEditProfile = Boolean(rider);
  const needsSteamLink = !steamIdResolved;
  const needsTlmpOAuth = !rider?.trucklineMpOAuthLinkedAt;
  const openAttendanceWindows = activeAttendanceEvents.filter((e) => e.isAttendanceOpen).length;
  const activeContractsCount = Array.isArray(contracts?.active) ? contracts.active.length : 0;
  const challengeDone = completions.length || progress.filter((p) => p.completed || p.percent >= 100).length;
  const challengeInProgress = progress.filter((p) => !(p.completed || p.percent >= 100)).length;

  const startAccountLink = async (provider) => {
    setLinkBusy(provider);
    setLinkBanner(null);
    try {
      const path = provider === 'steam' ? '/auth/steam/link/start' : '/auth/tlmp/link/start';
      const { data: res } = await axiosInstance.get(path);
      if (!res?.url) throw new Error('Missing authorize URL');
      window.location.href = res.url;
    } catch (e) {
      setLinkBanner({
        severity: 'error',
        text: e?.response?.data?.message || e.message || `Failed to start ${provider} linking`,
      });
      setLinkBusy('');
    }
  };

  const openEditProfile = () => {
    setEditError('');
    setEditForm({
      name: rider?.name || '',
      tmpIngameName: rider?.tmpIngameName || '',
      avatar: rider?.avatar || '',
      age: rider?.age != null ? String(rider.age) : '',
      country: rider?.country || '',
      steamID: rider?.steamID64 || rider?.steamID || user?.steamId || '',
      truckersmpId: rider?.truckersmpId || '',
      truckershubId: rider?.truckershubId || '',
      trucklineMpId: rider?.trucklineMpId || '',
    });
    setEditOpen(true);
  };

  const saveEditProfile = async () => {
    setEditSaving(true);
    setEditError('');
    try {
      await axiosInstance.patch('/me/profile', {
        name: editForm.name,
        tmpIngameName: editForm.tmpIngameName,
        avatar: editForm.avatar,
        age: editForm.age,
        country: editForm.country,
        steamID: editForm.steamID,
        steamID64: editForm.steamID,
        truckersmpId: editForm.truckersmpId,
        truckershubId: editForm.truckershubId,
        trucklineMpId: editForm.trucklineMpId,
      });
      await reloadDashboard();
      setEditOpen(false);
    } catch (e) {
      setEditError(e?.response?.data?.message || 'Failed to save profile');
    } finally {
      setEditSaving(false);
    }
  };

  const refreshWallet = async () => {
    try {
      const w = await getMyWallet();
      setWallet({
        balance: Number(w.balance || 0),
        transactions: Array.isArray(w.transactions) ? w.transactions : [],
      });
    } catch (e) { console.log(e); }
  };

  return (
    <MagicPageShell>
      <Box sx={{ minHeight: '100vh', bgcolor: 'transparent', fontFamily: font }}>
        {pendingDivisionInvites > 0 && (
          <Container maxWidth="xl" sx={{ pt: 2 }}>
            <Alert
              severity="info"
              action={(
                <Button component={RouterLink} to="/division/invites" size="small" sx={{ textTransform: 'none' }}>
                  View invites
                </Button>
              )}
            >
              You have {pendingDivisionInvites} division invitation(s).
            </Alert>
          </Container>
        )}
        {data?.rto?.showBanner && (data?.rto?.outstanding || 0) > 0 && (
          <Container maxWidth="xl" sx={{ pt: 2 }}>
            <Alert
              severity="warning"
              action={(
                <Button component={RouterLink} to="/rto/my-challans" size="small" sx={{ textTransform: 'none' }}>
                  Pay fines
                </Button>
              )}
            >
              You have {Number(data.rto.outstanding).toLocaleString()} tokens in outstanding RTO fines.
            </Alert>
          </Container>
        )}
        {linkBanner && (
          <Container maxWidth="xl" sx={{ pt: 2 }}>
            <Alert severity={linkBanner.severity} onClose={() => setLinkBanner(null)}>
              {linkBanner.text}
            </Alert>
          </Container>
        )}

        <Container maxWidth="xl" sx={{ pt: { xs: 2, md: 3 }, pb: 6 }}>
          <RiderOnboardingChecklist
            data={data}
            divisionSummary={divisionSummary}
            invitesCount={pendingDivisionInvites}
          />

          {data?.dailyLoginBonus?.enabled && (
            <DailyLoginBonusBanner
              dailyLoginBonus={data.dailyLoginBonus}
              weeklyLoginChest={data.weeklyLoginChest}
              awardOpen={loginBonusDialogOpen}
              onCloseAward={() => setLoginBonusDialogOpen(false)}
              font={font}
              colors={T}
            />
          )}

          {/* ── Hero ─────────────────────────────────────────── */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              p: { xs: 2.25, md: 3.25 },
              borderRadius: 3,
              border: `1px solid ${alpha(T.accent, 0.28)}`,
              background: `
                radial-gradient(ellipse 70% 80% at 0% 0%, ${alpha(T.accent, 0.16)} 0%, transparent 55%),
                radial-gradient(ellipse 50% 60% at 100% 0%, ${alpha(T.info, 0.12)} 0%, transparent 50%),
                linear-gradient(160deg, ${T.surfaceElevated} 0%, ${T.surface} 55%, #0A0A0C 100%)
              `,
              mb: 2.5,
            }}
          >
            <AnimatedGridPattern
              size={36}
              squares={12}
              lineColor="rgba(228,255,26,0.05)"
              glowColor="rgba(228,255,26,0.12)"
            />
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={2.5}
              alignItems={{ lg: 'center' }}
              justifyContent="space-between"
              sx={{ position: 'relative', zIndex: 1 }}
            >
              <Stack direction="row" spacing={1.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Avatar
                  src={rider?.avatar}
                  sx={{
                    width: { xs: 64, md: 76 },
                    height: { xs: 64, md: 76 },
                    border: `2px solid ${alpha(T.accent, 0.55)}`,
                    boxShadow: `0 0 28px ${alpha(T.accent, 0.18)}`,
                  }}
                >
                  {firstName.charAt(0)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontFamily: font, fontSize: '0.78rem', color: T.accent, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Driver command center
                  </Typography>
                  <Typography sx={{ fontFamily: font, fontWeight: 900, fontSize: { xs: '1.35rem', md: '1.75rem' }, letterSpacing: '-0.03em', lineHeight: 1.15 }}>
                    {greeting}, {firstName}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 0.85 }}>
                    <TagChip label={user?.role || 'Member'} color={T.accent} filled />
                    {rider?.employeeID && <TagChip label={rider.employeeID} color={T.textSecondary} />}
                    <StatusBadge label={passStatusLabel} color={passStatusColor} />
                    {activeDivision && <TagChip label={activeDivision.name} color={T.info} filled />}
                    <Typography sx={{ fontFamily: font, fontSize: '0.8rem', color: T.textMuted }}>
                      ★ {rider?.rating || 4.8}
                    </Typography>
                  </Stack>
                  <Box sx={{ mt: 1.1 }}>
                    <ConnectedAccountBadges
                      steamId={steamIdResolved}
                      truckersmpId={rider?.truckersmpId}
                      truckershubId={rider?.truckershubId}
                      trucklineMpId={rider?.trucklineMpId}
                      trucklineMpOAuthLinkedAt={rider?.trucklineMpOAuthLinkedAt}
                      onConnectSteam={needsSteamLink ? () => startAccountLink('steam') : undefined}
                      onConnectTruckline={needsTlmpOAuth ? () => startAccountLink('tlmp') : undefined}
                    />
                  </Box>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ maxWidth: { lg: 420 } }}>
                {canEditProfile && (
                  <Button variant="outlined" size="small" startIcon={<EditOutlinedIcon />} onClick={openEditProfile} sx={{ textTransform: 'none' }}>
                    Edit profile
                  </Button>
                )}
                <Button
                  component={RouterLink}
                  to="/wallet#driver-pass"
                  variant="contained"
                  size="small"
                  startIcon={<ConfirmationNumberOutlinedIcon />}
                  sx={{
                    textTransform: 'none', fontWeight: 700,
                    bgcolor: T.accent, color: '#0A0A0A',
                    '&:hover': { bgcolor: '#c8e600' },
                  }}
                >
                  {driverPass?.valid ? 'Manage pass' : 'Get driver pass'}
                </Button>
                <Button component={RouterLink} to="/jobs" variant="outlined" size="small" sx={{ textTransform: 'none' }}>
                  My jobs
                </Button>
                <Button component={RouterLink} to="/leaderboard" variant="outlined" size="small" sx={{ textTransform: 'none' }}>
                  Leaderboard
                </Button>
              </Stack>
            </Stack>

            <Grid container spacing={1.25} sx={{ mt: 2.5, position: 'relative', zIndex: 1 }}>
              {[
                { label: 'Tokens', value: Number(wallet?.balance || 0), color: T.accent },
                { label: 'Jobs', value: thTotals.totalJobs, color: T.info },
                { label: 'Income', value: thTotals.totalRevenue, color: T.success },
                { label: 'Distance km', value: thTotals.totalDistance, color: T.warning },
                { label: 'Events', value: attendance?.totalEventsAttended || 0, color: T.info },
                { label: 'Challenges', value: challengeDone, color: T.accent },
              ].map((kpi) => (
                <Grid item xs={6} sm={4} md={2} key={kpi.label}>
                  <Box sx={{
                    p: '12px 14px',
                    borderRadius: T.radiusSm,
                    border: `1px solid ${T.border}`,
                    bgcolor: alpha('#000', 0.28),
                    backdropFilter: 'blur(8px)',
                  }}>
                    <Typography sx={{ ...sxLabel, fontSize: '0.68rem', mb: 0.4 }}>{kpi.label}</Typography>
                    <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: '1.2rem', color: kpi.color, letterSpacing: '-0.02em' }}>
                      <NumberTicker value={kpi.value} />
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* ── Sticky section tabs ──────────────────────────── */}
          <Box sx={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            mb: 2.5,
            py: 1,
            bgcolor: alpha(T.bg, 0.82),
            backdropFilter: 'blur(14px)',
            borderBottom: `1px solid ${T.border}`,
          }}>
            <Tabs
              value={dashTab}
              onChange={(_, v) => setDashTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 42,
                '& .MuiTabs-indicator': { bgcolor: T.accent, height: 2 },
                '& .MuiTab-root': {
                  fontFamily: font,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.88rem',
                  color: T.textMuted,
                  minHeight: 42,
                  px: 1.75,
                  '&.Mui-selected': { color: T.accent },
                },
              }}
            >
              {DASH_TABS.map((t) => (
                <Tab
                  key={t.id}
                  value={t.id}
                  icon={t.icon}
                  iconPosition="start"
                  label={(
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <span>{t.label}</span>
                      {t.id === 'events' && openAttendanceWindows > 0 && (
                        <Chip size="small" label={openAttendanceWindows} sx={{ height: 18, fontSize: '0.65rem', bgcolor: T.accentDim, color: T.accent }} />
                      )}
                      {t.id === 'drive' && activeContractsCount > 0 && (
                        <Chip size="small" label={activeContractsCount} sx={{ height: 18, fontSize: '0.65rem', bgcolor: T.infoDim, color: T.info }} />
                      )}
                    </Stack>
                  )}
                />
              ))}
            </Tabs>
          </Box>

          <Fade in timeout={320}>
            <Box>
              <AnimatedTabPanel panelKey={dashTab}>
                {dashTab === 'overview' && (
                  <Stack spacing={3}>
                    <BentoGrid minItemWidth={200} gap={2}>
                      {[
                        { icon: <AccountBalanceWallet sx={{ fontSize: 17 }} />, label: 'Token Balance', value: Number(wallet?.balance || 0), accent: true, ticker: true },
                        { icon: <LocalShippingOutlinedIcon sx={{ fontSize: 17 }} />, label: 'Total Jobs', value: thTotals.totalJobs, tint: T.infoDim, ticker: true },
                        { icon: <AttachMoneyOutlinedIcon sx={{ fontSize: 17 }} />, label: 'Total Income', value: thTotals.totalRevenue, tint: T.successDim, ticker: true },
                        { icon: <PlaceOutlinedIcon sx={{ fontSize: 17 }} />, label: 'Distance (km)', value: thTotals.totalDistance, tint: T.warningDim, ticker: true },
                        { icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 17 }} />, label: 'Events attended', value: attendance?.totalEventsAttended || 0, tint: T.infoDim, ticker: true },
                        { icon: <FlagOutlinedIcon sx={{ fontSize: 17 }} />, label: 'Challenges done', value: challengeDone, tint: T.accentDim, ticker: true },
                      ].map((kpi, idx) => (
                        <BentoItem key={kpi.label}>
                          <Box component={motion.div} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05, duration: 0.25 }}>
                            <StatCard {...kpi} />
                          </Box>
                        </BentoItem>
                      ))}
                    </BentoGrid>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={7}>
                        <Card sx={{ ...sxCard, height: '100%' }}>
                          <CardContent sx={{ p: '20px !important' }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                              <Typography sx={sxLabel}>Driver pass</Typography>
                              <StatusBadge label={passStatusLabel} color={passStatusColor} />
                            </Stack>
                            <Typography sx={{ fontFamily: font, fontSize: '1.1rem', fontWeight: 700, color: T.text }}>
                              {driverPass?.activePassTypeLabel || 'No active pass'}
                            </Typography>
                            <Typography sx={{ fontFamily: font, fontSize: '0.9rem', color: T.textSecondary, mt: 0.75 }}>
                              {driverPass?.expiresAt
                                ? `Valid until ${new Date(driverPass.expiresAt).toLocaleDateString()}`
                                : driverPass?.enabled === false
                                  ? 'Passes are disabled for now.'
                                  : 'A valid pass unlocks full token payouts, stats, and progression.'}
                            </Typography>
                            {driverPass?.enabled !== false && (
                              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                                <Button
                                  component={RouterLink}
                                  to="/wallet#driver-pass"
                                  variant="contained"
                                  size="small"
                                  startIcon={<ConfirmationNumberOutlinedIcon />}
                                  sx={{ textTransform: 'none', fontWeight: 700, bgcolor: T.accent, color: '#0A0A0A', '&:hover': { bgcolor: '#c8e600' } }}
                                >
                                  {driverPass?.valid ? 'View pass store' : 'Buy a pass'}
                                </Button>
                                <Button component={RouterLink} to="/wallet" size="small" variant="outlined" sx={{ textTransform: 'none' }}>
                                  Open wallet
                                </Button>
                              </Stack>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <Card sx={{ ...sxCard, height: '100%', background: `linear-gradient(145deg, ${alpha(T.info, 0.08)}, ${T.surface})` }}>
                          <CardContent sx={{ p: '20px !important' }}>
                            <Typography sx={sxLabel}>Live pulse</Typography>
                            <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                              {[
                                { label: 'Open attendance', value: openAttendanceWindows, go: () => setDashTab('events') },
                                { label: 'Active contracts', value: activeContractsCount, go: () => setDashTab('drive') },
                                { label: 'Challenges in progress', value: challengeInProgress, go: () => setDashTab('career') },
                                { label: 'Today’s events', value: todayEvents.length, go: () => setDashTab('events') },
                              ].map((row) => (
                                <Stack
                                  key={row.label}
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  onClick={row.go}
                                  sx={{
                                    p: '10px 12px',
                                    borderRadius: T.radiusSm,
                                    border: `1px solid ${T.border}`,
                                    bgcolor: T.surfaceAlt,
                                    cursor: 'pointer',
                                    '&:hover': { borderColor: T.borderHover },
                                  }}
                                >
                                  <Typography sx={{ fontFamily: font, fontSize: '0.85rem', color: T.textSecondary }}>{row.label}</Typography>
                                  <Typography sx={{ fontFamily: font, fontWeight: 800, color: T.text }}>{row.value}</Typography>
                                </Stack>
                              ))}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {activeDivision && (
                      <RevealSection>
                        <Card sx={sxCard}>
                          {divisionSummary?.division?.bannerUrl && (
                            <Box sx={{
                              height: 96,
                              background: `url(${divisionSummary.division.bannerUrl}) center/cover no-repeat`,
                              position: 'relative',
                            }}>
                              <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(9,9,11,0.92), rgba(9,9,11,0.15))' }} />
                            </Box>
                          )}
                          <CardContent sx={{ p: '20px !important', mt: divisionSummary?.division?.bannerUrl ? -4 : 0, position: 'relative' }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                              <Avatar
                                src={activeDivision.logoUrl || undefined}
                                sx={{ width: 56, height: 56, border: `3px solid ${T.surface}`, bgcolor: T.surfaceElevated }}
                              >
                                {activeDivision.name?.[0] || 'D'}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ ...sxLabel, display: 'block' }}>Your division</Typography>
                                <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: '1.15rem', color: T.text }} noWrap>
                                  {activeDivision.name}
                                  {divisionSummary?.division?.isLeader && (
                                    <Typography component="span" sx={{ ml: 1, fontSize: '0.72rem', color: T.accent, fontWeight: 700 }}>· LEADER</Typography>
                                  )}
                                </Typography>
                                {divisionSummary?.me && (
                                  <Typography sx={{ fontFamily: font, fontSize: '0.8rem', color: T.textMuted }}>
                                    Rank #{divisionSummary.me?.rank || '—'} · {divisionSummary.division?.memberCount || 0} members · {divisionSummary.division?.taxPercent || 0}% tax
                                  </Typography>
                                )}
                              </Box>
                              <Stack direction="row" spacing={1}>
                                {activeDivision.slug && (
                                  <Button size="small" variant="outlined" component={RouterLink} to={`/divisions/${activeDivision.slug}`} sx={{ textTransform: 'none' }}>
                                    Public page
                                  </Button>
                                )}
                                <Button size="small" variant="contained" component={RouterLink} to="/division" sx={{ textTransform: 'none', bgcolor: T.accent, color: '#000', '&:hover': { bgcolor: '#c8e600' } }}>
                                  Open division
                                </Button>
                              </Stack>
                            </Stack>
                            {divisionSummary?.me?.stats && (
                              <Grid container spacing={1.25} sx={{ mt: 2 }}>
                                {[
                                  { label: 'My jobs', value: (divisionSummary.me.stats.totalJobs || 0).toLocaleString() },
                                  { label: 'My revenue', value: Math.round(divisionSummary.me.stats.totalRevenue || 0).toLocaleString() },
                                  { label: 'Tax contributed', value: Math.round(divisionSummary.me.stats.totalTaxContributed || 0).toLocaleString() },
                                  { label: 'Division wallet', value: Math.round(divisionSummary.division.walletBalance || 0).toLocaleString() },
                                ].map((c) => (
                                  <Grid item xs={6} sm={3} key={c.label}>
                                    <InfoCell label={c.label} value={c.value} />
                                  </Grid>
                                ))}
                              </Grid>
                            )}
                          </CardContent>
                        </Card>
                      </RevealSection>
                    )}

                    {todayEvents.length > 0 && (
                      <RevealSection>
                        <Card sx={sxCard}>
                          <CardContent sx={{ p: '20px !important' }}>
                            <SectionTitle
                              action={(
                                <Button
                                  component={RouterLink}
                                  to="/calendar"
                                  size="small"
                                  startIcon={<CalendarMonthOutlinedIcon sx={{ fontSize: 14 }} />}
                                  sx={{ fontFamily: font, fontSize: '0.82rem', textTransform: 'none', color: T.accent }}
                                >
                                  Full calendar
                                </Button>
                              )}
                            >
                              Today&apos;s convoy
                            </SectionTitle>
                            <Box sx={{
                              display: 'flex', gap: 2, overflowX: 'auto', scrollSnapType: 'x mandatory', pb: 1,
                              '&::-webkit-scrollbar': { height: 6 },
                              '&::-webkit-scrollbar-thumb': { bgcolor: T.border, borderRadius: 3 },
                            }}>
                              {todayEvents.map((ev, idx) => {
                                const meetupDate = ev.meetupTime ? new Date(ev.meetupTime) : null;
                                const meetupLabel = meetupDate && !Number.isNaN(meetupDate.getTime())
                                  ? meetupDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                  : 'TBD';
                                return (
                                  <Box
                                    key={ev.id || `${ev.title}-${idx}`}
                                    sx={{
                                      flexShrink: 0, width: { xs: 280, sm: 320 }, scrollSnapAlign: 'start',
                                      p: 1.5, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, bgcolor: T.surfaceAlt,
                                    }}
                                  >
                                    {ev.slotImageUrl ? (
                                      <Box component="img" src={ev.slotImageUrl} sx={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: T.radiusXs, mb: 1.25 }} />
                                    ) : (
                                      <Box sx={{ height: 140, mb: 1.25, borderRadius: T.radiusXs, bgcolor: T.accentDim, display: 'grid', placeItems: 'center' }}>
                                        <Typography sx={{ color: T.accent, fontWeight: 600, fontSize: '0.8rem' }}>No image</Typography>
                                      </Box>
                                    )}
                                    <Typography sx={{ fontFamily: font, fontWeight: 700, color: T.text }} noWrap>{ev.title}</Typography>
                                    <Typography sx={{ fontFamily: font, fontSize: '0.8rem', color: T.textMuted, mt: 0.35 }}>
                                      {ev.slotNumber != null ? `Slot ${ev.slotNumber}` : 'Slot TBD'}
                                      {ev.slotName ? ` · ${ev.slotName}` : ''} · {meetupLabel}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </CardContent>
                        </Card>
                      </RevealSection>
                    )}

                    <RevealSection>
                      <Card sx={sxCard}>
                        <CardContent sx={{ p: '20px !important' }}>
                          <SectionTitle
                            action={(
                              <Button size="small" onClick={() => setDashTab('drive')} sx={{ textTransform: 'none', color: T.accent }}>
                                Go to Drive →
                              </Button>
                            )}
                          >
                            Launchpad
                          </SectionTitle>
                          <Grid container spacing={1.25}>
                            {QUICK_LINKS.map((link, i) => (
                              <Grid item xs={12} sm={6} md={4} lg={3} key={link.to}>
                                <ActionTile {...link} accent={i < 2} />
                              </Grid>
                            ))}
                          </Grid>
                        </CardContent>
                      </Card>
                    </RevealSection>
                  </Stack>
                )}

                {dashTab === 'drive' && (
                  <Stack spacing={3}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} lg={7}>
                        <Card sx={{ ...sxCard, height: '100%' }}>
                          <CardContent sx={{ p: '20px !important' }}>
                            <SectionTitle>Revenue & distance (6 mo)</SectionTitle>
                            <Box sx={{ height: 260 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartPack.revenueData}>
                                  <defs>
                                    <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="0%" stopColor={T.accent} stopOpacity={0.35} />
                                      <stop offset="100%" stopColor={T.accent} stopOpacity={0} />
                                    </linearGradient>
                                  </defs>
                                  <CartesianGrid stroke={T.border} strokeDasharray="3 3" />
                                  <XAxis dataKey="month" stroke={T.textMuted} tick={{ fill: T.textMuted, fontSize: 12 }} />
                                  <YAxis stroke={T.textMuted} tick={{ fill: T.textMuted, fontSize: 12 }} />
                                  <Tooltip content={<ChartTooltip />} />
                                  <Area type="monotone" dataKey="revenue" name="Revenue" stroke={T.accent} fill="url(#revFill)" strokeWidth={2} />
                                  <Area type="monotone" dataKey="distance" name="Distance" stroke={T.info} fill="transparent" strokeWidth={1.5} />
                                </AreaChart>
                              </ResponsiveContainer>
                            </Box>
                            {!chartPack.hasData && (
                              <Typography sx={{ fontFamily: font, fontSize: '0.8rem', color: T.textMuted, mt: 1 }}>
                                Charts update as you deliver jobs.
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} lg={5}>
                        <Card sx={{ ...sxCard, height: '100%' }}>
                          <CardContent sx={{ p: '20px !important' }}>
                            <SectionTitle>Jobs by weekday</SectionTitle>
                            <Box sx={{ height: 260 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartPack.weeklyData}>
                                  <CartesianGrid stroke={T.border} strokeDasharray="3 3" vertical={false} />
                                  <XAxis dataKey="day" stroke={T.textMuted} tick={{ fill: T.textMuted, fontSize: 12 }} />
                                  <YAxis allowDecimals={false} stroke={T.textMuted} tick={{ fill: T.textMuted, fontSize: 12 }} />
                                  <Tooltip content={<ChartTooltip />} />
                                  <Bar dataKey="jobs" name="Jobs" fill={T.info} radius={[4, 4, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                    <BentoGrid minItemWidth={340} gap={2}>
                      <BentoItem>
                        <ActiveContracts onRefresh={() => myContracts().then((res) => setContracts(res))} />
                      </BentoItem>
                      <BentoItem>
                        <CompletedContracts onRefresh={() => myContracts().then((res) => setContracts(res))} />
                      </BentoItem>
                    </BentoGrid>

                    <RevealSection>
                      <WalletTransactions wallet={wallet} onRefresh={refreshWallet} />
                    </RevealSection>
                  </Stack>
                )}

                {dashTab === 'events' && (
                  <Stack spacing={3}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} lg={7}>
                        <Card sx={sxCard}>
                          <CardContent sx={{ p: '20px !important' }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                              <Typography sx={sxLabel}>Events & attendance</Typography>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                  size="small"
                                  component={RouterLink}
                                  to="/attendance/me?tab=streak"
                                  startIcon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />}
                                  variant="outlined"
                                  sx={{ fontFamily: font, fontSize: '0.75rem', fontWeight: 600, color: T.accent, borderColor: alpha(T.accent, 0.35), textTransform: 'none' }}
                                >
                                  Streak & rewards
                                </Button>
                                <StatusBadge label="TruckersMP" color={T.success} />
                              </Stack>
                            </Stack>

                            <Box sx={{ p: '16px', mb: 2.5, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, bgcolor: T.surfaceAlt }}>
                              <Typography sx={{ ...sxLabel, fontSize: '0.72rem' }}>Total events attended</Typography>
                              <Typography sx={{ ...sxValue, fontSize: '2.25rem', mt: 0.5 }}>
                                <NumberTicker value={attendance?.totalEventsAttended || 0} />
                              </Typography>
                            </Box>

                            {attendanceSubmitMsg && (
                              <Alert severity="success" sx={{ mb: 1.5 }}>{attendanceSubmitMsg}</Alert>
                            )}

                            <Typography sx={{ ...sxLabel, mb: 1.25 }}>Recently attended</Typography>
                            <Stack spacing={1} sx={{ mb: 2.5 }}>
                              {(attendance?.eventsAttended || []).slice(0, 5).map((e) => (
                                <Stack
                                  key={e.id}
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  sx={{
                                    p: '12px 14px', border: `1px solid ${T.border}`,
                                    borderRadius: T.radiusSm, bgcolor: T.surfaceAlt,
                                    '&:hover': { borderColor: T.borderHover },
                                  }}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontFamily: font, fontSize: '0.95rem', fontWeight: 600, color: T.text }} noWrap>
                                      {e.title || 'Event'}
                                    </Typography>
                                    <Typography sx={{ fontFamily: font, fontSize: '0.82rem', color: T.textMuted, mt: 0.25 }}>
                                      {e.eventDate ? new Date(e.eventDate).toLocaleDateString() : '—'}
                                      {e.approvedAt ? ` · Approved ${new Date(e.approvedAt).toLocaleDateString()}` : ''}
                                    </Typography>
                                  </Box>
                                  <StatusBadge label="Approved" color={T.success} />
                                </Stack>
                              ))}
                              {(attendance?.eventsAttended || []).length === 0 && (
                                <Typography sx={{ fontFamily: font, fontSize: '0.92rem', color: T.textMuted, py: 1 }}>
                                  No events recorded yet — join a convoy and mark attendance.
                                </Typography>
                              )}
                              {(attendance?.eventsAttended || []).length > 5 && (
                                <Button
                                  onClick={() => { setAttendanceOpen(true); setAttendancePage(1); }}
                                  sx={{
                                    fontFamily: font, alignSelf: 'flex-start', fontSize: '0.85rem',
                                    color: T.accent, textTransform: 'none', p: 0, minWidth: 0,
                                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                                  }}
                                >
                                  View all →
                                </Button>
                              )}
                            </Stack>

                            <Typography sx={{ ...sxLabel, mb: 1.25 }}>Active attendance windows</Typography>
                            <Stack spacing={1} sx={{ maxHeight: 340, overflowY: 'auto', pr: 0.5 }}>
                              {activeAttendanceEvents.map((ev) => (
                                <Stack
                                  key={ev._id}
                                  direction={{ xs: 'column', sm: 'row' }}
                                  alignItems={{ sm: 'center' }}
                                  justifyContent="space-between"
                                  spacing={1}
                                  sx={{
                                    p: '12px 14px', border: `1px solid ${T.border}`,
                                    borderRadius: T.radiusSm, bgcolor: T.surfaceAlt,
                                  }}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontFamily: font, fontSize: '0.95rem', fontWeight: 600, color: T.text }} noWrap>
                                      {ev.title}
                                    </Typography>
                                    <Typography sx={{ fontFamily: font, fontSize: '0.82rem', color: T.textMuted, mt: 0.25 }}>
                                      {new Date(ev.eventDate).toLocaleString()}
                                      {ev.endDate ? ` → ${new Date(ev.endDate).toLocaleString()}` : ''}
                                    </Typography>
                                  </Box>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <StatusBadge
                                      label={ev.isAttendanceOpen ? 'Open' : 'Closed'}
                                      color={ev.isAttendanceOpen ? T.success : T.textMuted}
                                    />
                                    <Button
                                      disabled={attendanceSubmitLoading || !ev.isAttendanceOpen}
                                      onClick={() => handleMarkAttendance(ev._id)}
                                      sx={{
                                        fontFamily: font, fontWeight: 600, fontSize: '0.82rem',
                                        bgcolor: ev.isAttendanceOpen ? T.accent : T.surfaceHover,
                                        color: ev.isAttendanceOpen ? T.bg : T.textMuted,
                                        borderRadius: T.radiusSm, px: 1.5, py: 0.75, textTransform: 'none',
                                        '&:hover': { bgcolor: ev.isAttendanceOpen ? '#c8e600' : T.surfaceHover },
                                        '&.Mui-disabled': { bgcolor: T.surfaceHover, color: T.textFaint },
                                      }}
                                    >
                                      I was there
                                    </Button>
                                  </Stack>
                                </Stack>
                              ))}
                              {activeAttendanceEvents.length === 0 && (
                                <Typography sx={{ fontFamily: font, fontSize: '0.92rem', color: T.textMuted, py: 1 }}>
                                  No active attendance windows right now.
                                </Typography>
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} lg={5}>
                        <Stack spacing={2}>
                          <Card sx={sxCard}>
                            <CardContent sx={{ p: '20px !important' }}>
                              <SectionTitle
                                action={(
                                  <Button component={RouterLink} to="/calendar" size="small" sx={{ textTransform: 'none', color: T.accent }}>
                                    Calendar
                                  </Button>
                                )}
                              >
                                Upcoming events
                              </SectionTitle>
                              <Stack spacing={0}>
                                {externalUpcoming.map((ev, idx) => (
                                  <Stack
                                    key={ev.id}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    spacing={1}
                                    sx={{
                                      py: 1.25,
                                      borderBottom: idx < externalUpcoming.length - 1 ? `1px solid ${T.border}` : 'none',
                                    }}
                                  >
                                    <Typography sx={{ fontFamily: font, fontSize: '0.9rem', color: T.text }} noWrap>
                                      {ev.title}
                                    </Typography>
                                    <Typography sx={{ fontFamily: font, fontSize: '0.78rem', color: T.textMuted, whiteSpace: 'nowrap' }}>
                                      {new Date(ev.start).toLocaleString(undefined, {
                                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                                      })}
                                    </Typography>
                                  </Stack>
                                ))}
                                {externalUpcoming.length === 0 && (
                                  <Typography sx={{ fontFamily: font, color: T.textMuted, fontSize: '0.9rem' }}>
                                    Nothing upcoming in the feed yet.
                                  </Typography>
                                )}
                              </Stack>
                            </CardContent>
                          </Card>

                          <Card sx={{ ...sxCard, background: `linear-gradient(150deg, ${alpha(T.accent, 0.1)}, ${T.surface})` }}>
                            <CardContent sx={{ p: '20px !important' }}>
                              <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: '1.05rem', mb: 0.75 }}>
                                Build your streak
                              </Typography>
                              <Typography sx={{ fontFamily: font, fontSize: '0.85rem', color: T.textSecondary, mb: 1.5 }}>
                                Attend convos, mark attendance while windows are open, and unlock streak rewards.
                              </Typography>
                              <Button
                                component={RouterLink}
                                to="/attendance/me"
                                variant="contained"
                                size="small"
                                sx={{ textTransform: 'none', fontWeight: 700, bgcolor: T.accent, color: '#0A0A0A', '&:hover': { bgcolor: '#c8e600' } }}
                              >
                                Open attendance hub
                              </Button>
                            </CardContent>
                          </Card>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Stack>
                )}

                {dashTab === 'career' && (
                  <Stack spacing={3}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <StatCard icon={<FlagOutlinedIcon sx={{ fontSize: 17 }} />} label="Challenges done" value={challengeDone} accent ticker />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <StatCard icon={<TrendingUpIcon sx={{ fontSize: 17 }} />} label="In progress" value={challengeInProgress} tint={T.infoDim} ticker />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <StatCard icon={<EmojiEventsOutlinedIcon sx={{ fontSize: 17 }} />} label="Achievements" value={achievements.length} tint={T.warningDim} ticker />
                      </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                      <Grid item xs={12} lg={6}>
                        <Card sx={{ ...sxCard, height: '100%' }}>
                          <CardContent sx={{ p: '20px !important' }}>
                            <SectionTitle
                              action={(
                                <Button component={RouterLink} to="/challenges" size="small" sx={{ textTransform: 'none', color: T.accent }}>
                                  Browse challenges
                                </Button>
                              )}
                            >
                              Challenge progress
                            </SectionTitle>
                            <Stack spacing={1}>
                              {progress.slice(0, 8).map((p) => {
                                const pct = Math.min(100, Math.round(p.percent || p.progressPercent || 0));
                                return (
                                  <Box
                                    key={p._id || p.challengeId}
                                    sx={{ p: '12px 14px', border: `1px solid ${T.border}`, borderRadius: T.radiusSm, bgcolor: T.surfaceAlt }}
                                  >
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                                      <Typography sx={{ fontFamily: font, fontWeight: 650, color: T.text, fontSize: '0.9rem' }} noWrap>
                                        {p.challengeName || p.name || 'Challenge'}
                                      </Typography>
                                      <Chip size="small" label={`${pct}%`} sx={{ height: 22, bgcolor: pct >= 100 ? T.successDim : T.accentDim, color: pct >= 100 ? T.success : T.accent }} />
                                    </Stack>
                                    <LinearProgress
                                      variant="determinate"
                                      value={pct}
                                      sx={{
                                        height: 4, borderRadius: 2, bgcolor: T.border,
                                        '& .MuiLinearProgress-bar': { bgcolor: pct >= 100 ? T.success : T.accent, borderRadius: 2 },
                                      }}
                                    />
                                  </Box>
                                );
                              })}
                              {progress.length === 0 && (
                                <Typography sx={{ fontFamily: font, color: T.textMuted, fontSize: '0.9rem', py: 2 }}>
                                  No challenge progress yet — pick one up and start hauling.
                                </Typography>
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} lg={6}>
                        <Card sx={{ ...sxCard, height: '100%' }}>
                          <CardContent sx={{ p: '20px !important' }}>
                            <SectionTitle>Recent completions</SectionTitle>
                            <Stack spacing={1}>
                              {completions.slice(0, 8).map((c) => (
                                <Stack
                                  key={c._id || `${c.challengeId}-${c.completionTime}`}
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  sx={{ p: '12px 14px', border: `1px solid ${T.border}`, borderRadius: T.radiusSm, bgcolor: T.surfaceAlt }}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontFamily: font, fontWeight: 650, color: T.text, fontSize: '0.9rem' }} noWrap>
                                      {c.challengeName || 'Challenge'}
                                    </Typography>
                                    <Typography sx={{ fontFamily: font, fontSize: '0.75rem', color: T.textMuted }}>
                                      {c.completionTime ? new Date(c.completionTime).toLocaleDateString() : '—'}
                                    </Typography>
                                  </Box>
                                  <StatusBadge label="Done" color={T.success} />
                                </Stack>
                              ))}
                              {completions.length === 0 && (
                                <Typography sx={{ fontFamily: font, color: T.textMuted, fontSize: '0.9rem', py: 2 }}>
                                  Completions will land here when you finish challenges.
                                </Typography>
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    <Card sx={sxCard}>
                      <CardContent sx={{ p: '20px !important' }}>
                        <SectionTitle
                          action={(
                            <Button component={RouterLink} to="/results" size="small" sx={{ textTransform: 'none', color: T.accent }}>
                              Results center
                            </Button>
                          )}
                        >
                          Achievements
                        </SectionTitle>
                        {achievements.length > 0 ? (
                          <Box sx={{
                            display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1,
                            '&::-webkit-scrollbar': { height: 6 },
                            '&::-webkit-scrollbar-thumb': { bgcolor: T.border, borderRadius: 3 },
                          }}>
                            {achievements.map((a) => (
                              <Box
                                key={a._id || a.name}
                                sx={{
                                  flexShrink: 0, width: 160, p: 1.5,
                                  border: `1px solid ${T.border}`, borderRadius: T.radiusSm, bgcolor: T.surfaceAlt, textAlign: 'center',
                                }}
                              >
                                <Avatar src={a.logoUrl || undefined} sx={{ width: 48, height: 48, mx: 'auto', mb: 1, bgcolor: T.warningDim, color: T.warning }}>
                                  <EmojiEventsOutlinedIcon />
                                </Avatar>
                                <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: '0.85rem', color: T.text }} noWrap>
                                  {a.name}
                                </Typography>
                                <Typography sx={{ fontFamily: font, fontSize: '0.7rem', color: T.textMuted, mt: 0.35 }}>
                                  {a.issuedOn ? new Date(a.issuedOn).toLocaleDateString() : '—'}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography sx={{ fontFamily: font, color: T.textMuted, fontSize: '0.9rem' }}>
                            Earn achievements through events, challenges, and milestones.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>

                    <Grid container spacing={2}>
                      <Grid item xs={12} lg={7}>
                        <Card sx={sxCard}>
                          <CardContent sx={{ p: '20px !important' }}>
                            <SectionTitle>TruckersHub snapshot</SectionTitle>
                            {truckershubStats && thSnapshot ? (
                              <>
                                <Typography sx={{ ...sxLabel, fontSize: '0.72rem', fontWeight: 400, mb: 2 }}>
                                  Last updated: {truckershubStats.lastUpdatedAt
                                    ? new Date(truckershubStats.lastUpdatedAt).toLocaleString()
                                    : '—'}
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                                  {[
                                    { label: 'Level', value: thSnapshot.level ?? '—' },
                                    { label: 'Avg Rating', value: thSnapshot.statistics?.rating?.avg ? thSnapshot.statistics.rating.avg.toFixed(2) : '—' },
                                    { label: 'THP Total', value: thSnapshot.statistics?.THP?.total ? thSnapshot.statistics.THP.total.toLocaleString() : '—' },
                                    { label: 'XP Total', value: thSnapshot.statistics?.XP?.total ? thSnapshot.statistics.XP.total.toLocaleString() : '—' },
                                    { label: 'Fuel Burned', value: thSnapshot.statistics?.fuelBurned?.total ? thSnapshot.statistics.fuelBurned.total.toLocaleString() : '—' },
                                    { label: 'Max Speed', value: thSnapshot.statistics?.speed?.max ? `${thSnapshot.statistics.speed.max.toFixed(1)} km/h` : '—' },
                                    { label: 'Avg / job', value: thTotals.avgPerJob.toLocaleString() },
                                    { label: 'Jobs (TH)', value: thTotals.totalJobs.toLocaleString() },
                                  ].map(({ label, value }) => (
                                    <InfoCell key={label} label={label} value={value} />
                                  ))}
                                </Box>
                              </>
                            ) : (
                              <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
                                <TrendingUpIcon sx={{ color: T.textFaint, fontSize: 28 }} />
                                <Typography sx={{ fontFamily: font, fontSize: '0.92rem', color: T.textMuted }}>
                                  No TruckersHub data available yet
                                </Typography>
                              </Stack>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} lg={5}>
                        <Card sx={{ ...sxCard, height: '100%', background: `linear-gradient(155deg, ${alpha(T.info, 0.12)}, ${T.surface})` }}>
                          <CardContent sx={{ p: '20px !important' }}>
                            <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: '1.15rem', mb: 0.75 }}>
                              Climb the ranks
                            </Typography>
                            <Typography sx={{ fontFamily: font, fontSize: '0.88rem', color: T.textSecondary, mb: 2 }}>
                              Compare distance, income, and attendance on the company leaderboard — or dig into your personal results center.
                            </Typography>
                            <Stack spacing={1}>
                              <Button component={RouterLink} to="/leaderboard" variant="contained" sx={{ textTransform: 'none', fontWeight: 700, bgcolor: T.accent, color: '#0A0A0A', '&:hover': { bgcolor: '#c8e600' } }}>
                                Open leaderboard
                              </Button>
                              <Button component={RouterLink} to="/results" variant="outlined" sx={{ textTransform: 'none' }}>
                                Results center
                              </Button>
                              <Button component={RouterLink} to="/goals" variant="outlined" sx={{ textTransform: 'none' }}>
                                Personal goals
                              </Button>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Stack>
                )}

                {dashTab === 'profile' && (
                  <Stack spacing={3}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} lg={7}>
                        <Card sx={sxCard}>
                          <CardContent>
                            <SectionTitle
                              action={canEditProfile ? (
                                <Button size="small" startIcon={<EditOutlinedIcon />} onClick={openEditProfile} sx={{ textTransform: 'none' }}>
                                  Edit
                                </Button>
                              ) : null}
                            >
                              Profile & game details
                            </SectionTitle>
                            <Grid container spacing={2.5}>
                              <Grid item xs={12} md={6}>
                                <Typography sx={{ ...sxLabel, mb: 1 }}>Platform IDs</Typography>
                                <Stack spacing={0.75}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={!steamIdResolved}
                                    component={steamIdResolved ? 'a' : 'button'}
                                    href={steamIdResolved ? `https://steamcommunity.com/profiles/${steamIdResolved}` : undefined}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                  >
                                    {steamIdResolved ? `Steam · ${steamIdResolved}` : 'Steam not linked'}
                                  </Button>
                                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!rider?.truckersmpId}
                                      component={rider?.truckersmpId ? 'a' : 'button'}
                                      href={rider?.truckersmpId ? `https://truckersmp.com/user/${rider.truckersmpId}` : undefined}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{ textTransform: 'none' }}
                                    >
                                      {rider?.truckersmpId ? `TMP · ${rider.truckersmpId}` : 'TMP not linked'}
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!rider?.truckershubId}
                                      component={rider?.truckershubId ? 'a' : 'button'}
                                      href={rider?.truckershubId ? `https://truckershub.in/profile/${rider.truckershubId}` : undefined}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{ textTransform: 'none' }}
                                    >
                                      {rider?.truckershubId ? `Hub · ${rider.truckershubId}` : 'Hub not linked'}
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!rider?.trucklineMpId}
                                      component={rider?.trucklineMpId ? 'a' : 'button'}
                                      href={rider?.trucklineMpId ? `https://trucklinemp.com/user/${rider.trucklineMpId}` : undefined}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{ textTransform: 'none' }}
                                    >
                                      {rider?.trucklineMpId
                                        ? `TLMP · ${rider.trucklineMpId}${rider?.trucklineMpOAuthLinkedAt ? '' : ' (ID only)'}`
                                        : 'TLMP not linked'}
                                    </Button>
                                  </Stack>
                                  {(needsSteamLink || needsTlmpOAuth) && (
                                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
                                      {needsSteamLink && (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          disabled={linkBusy === 'steam'}
                                          onClick={() => startAccountLink('steam')}
                                          sx={{
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            bgcolor: T.accent,
                                            color: '#0A0A0A',
                                            '&:hover': { bgcolor: '#c8e600' },
                                          }}
                                        >
                                          {linkBusy === 'steam' ? 'Opening Steam…' : 'Link Steam account'}
                                        </Button>
                                      )}
                                      {needsTlmpOAuth && (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          disabled={linkBusy === 'tlmp'}
                                          onClick={() => startAccountLink('tlmp')}
                                          sx={{
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            bgcolor: T.info,
                                            color: '#fff',
                                            '&:hover': { bgcolor: alpha(T.info, 0.85) },
                                          }}
                                        >
                                          {linkBusy === 'tlmp' ? 'Opening Truckline MP…' : 'Link Truckline MP account'}
                                        </Button>
                                      )}
                                    </Stack>
                                  )}
                                </Stack>
                                <Divider sx={{ my: 2, borderColor: T.border }} />
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                  <InfoCell label="Country" value={rider?.country || '—'} />
                                  <InfoCell label="Age" value={rider?.age ?? '—'} />
                                  <InfoCell label="In-game" value={rider?.tmpIngameName || '—'} />
                                  <InfoCell label="Employee ID" value={rider?.employeeID || '—'} />
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Box sx={{ mb: 2.5 }}>
                                  <Typography sx={{ ...sxLabel, mb: 1 }}>DLCs owned</Typography>
                                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                    <TagChip
                                      label={rider?.dlcsOwned?.ets2?.length > 0 ? `ETS2 ×${rider.dlcsOwned.ets2.length}` : 'No ETS2 DLCs'}
                                      color={rider?.dlcsOwned?.ets2?.length > 0 ? T.warning : T.textMuted}
                                      filled={rider?.dlcsOwned?.ets2?.length > 0}
                                    />
                                    <TagChip
                                      label={rider?.dlcsOwned?.ats?.length > 0 ? `ATS ×${rider.dlcsOwned.ats.length}` : 'No ATS DLCs'}
                                      color={rider?.dlcsOwned?.ats?.length > 0 ? T.warning : T.textMuted}
                                      filled={rider?.dlcsOwned?.ats?.length > 0}
                                    />
                                  </Stack>
                                </Box>
                                <Box>
                                  <Typography sx={{ ...sxLabel, mb: 1 }}>Games</Typography>
                                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                    {rider?.gamesOwned?.length > 0
                                      ? rider.gamesOwned.map((g, i) => <TagChip key={i} label={g} color={T.warning} filled />)
                                      : <TagChip label="No games listed" />}
                                  </Stack>
                                </Box>
                                {(user?.ownedTrucks || []).length > 0 && (
                                  <Box sx={{ mt: 2.5 }}>
                                    <Typography sx={{ ...sxLabel, mb: 1 }}>Owned trucks</Typography>
                                    <Typography sx={{ fontFamily: font, color: T.textSecondary, fontSize: '0.9rem' }}>
                                      {(user.ownedTrucks || []).length} truck(s) in garage
                                    </Typography>
                                    <Button component={RouterLink} to="/fleet" size="small" sx={{ mt: 1, textTransform: 'none', color: T.accent }}>
                                      Open fleet →
                                    </Button>
                                  </Box>
                                )}
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} lg={5}>
                        <Stack spacing={2}>
                          <Card sx={{ ...sxCard, background: `linear-gradient(150deg, ${alpha(T.accent, 0.1)}, ${T.surface})` }}>
                            <CardContent sx={{ p: '20px !important' }}>
                              <Typography sx={{ fontFamily: font, fontWeight: 800, mb: 0.75 }}>Keep your hub useful</Typography>
                              <Typography sx={{ fontFamily: font, fontSize: '0.85rem', color: T.textSecondary, mb: 1.5 }}>
                                Link Steam and Truckline MP so stats, attendance, and challenges stay accurate.
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {needsSteamLink && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    disabled={linkBusy === 'steam'}
                                    onClick={() => startAccountLink('steam')}
                                    sx={{ textTransform: 'none', fontWeight: 700, bgcolor: T.accent, color: '#0A0A0A', '&:hover': { bgcolor: '#c8e600' } }}
                                  >
                                    {linkBusy === 'steam' ? 'Opening Steam…' : 'Link Steam account'}
                                  </Button>
                                )}
                                {needsTlmpOAuth && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    disabled={linkBusy === 'tlmp'}
                                    onClick={() => startAccountLink('tlmp')}
                                    sx={{ textTransform: 'none', fontWeight: 700, bgcolor: T.info, color: '#fff', '&:hover': { bgcolor: alpha(T.info, 0.85) } }}
                                  >
                                    {linkBusy === 'tlmp' ? 'Opening Truckline MP…' : 'Link Truckline MP account'}
                                  </Button>
                                )}
                                {!needsSteamLink && !needsTlmpOAuth && (
                                  <Button variant="contained" size="small" onClick={openEditProfile} sx={{ textTransform: 'none', fontWeight: 700, bgcolor: T.accent, color: '#0A0A0A', '&:hover': { bgcolor: '#c8e600' } }}>
                                    Update linked accounts
                                  </Button>
                                )}
                              </Stack>
                            </CardContent>
                          </Card>
                          <Card sx={sxCard}>
                            <CardContent sx={{ p: '20px !important' }}>
                              <SectionTitle>Shortcuts</SectionTitle>
                              <Stack spacing={1}>
                                {[
                                  { label: 'Change password', to: '/change-password' },
                                  { label: 'My bookings', to: '/my-bookings' },
                                  { label: 'Loans', to: '/loans' },
                                  { label: 'RTO challans', to: '/rto/my-challans' },
                                ].map((s) => (
                                  <Button
                                    key={s.to}
                                    component={RouterLink}
                                    to={s.to}
                                    endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                      justifyContent: 'space-between',
                                      textTransform: 'none',
                                      color: T.text,
                                      border: `1px solid ${T.border}`,
                                      borderRadius: T.radiusSm,
                                      px: 1.5,
                                      '&:hover': { borderColor: T.borderHover, bgcolor: T.surfaceHover },
                                    }}
                                  >
                                    {s.label}
                                  </Button>
                                ))}
                              </Stack>
                            </CardContent>
                          </Card>
                        </Stack>
                      </Grid>
                    </Grid>

                    <RevealSection>
                      <LicenseCard userData={user} riderData={rider} />
                    </RevealSection>
                  </Stack>
                )}
              </AnimatedTabPanel>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* ── Attendance modal ───────────────────────────────── */}
      <Dialog
        open={attendanceOpen}
        onClose={() => setAttendanceOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            bgcolor: T.surface, border: `1px solid ${T.borderStrong}`,
            borderRadius: T.radius, boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
          },
        }}
      >
        <DialogTitle sx={{ p: '20px 24px', borderBottom: `1px solid ${T.border}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: '1.15rem', color: T.text }}>
              All attended events
            </Typography>
            <IconButton onClick={() => setAttendanceOpen(false)} size="small" sx={{ color: T.textMuted, '&:hover': { color: T.text } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: '20px 24px' }}>
          {(() => {
            const all = attendance?.eventsAttended || [];
            const totalPages = Math.max(1, Math.ceil(all.length / pageSize));
            const clampedPage = Math.min(attendancePage, totalPages);
            const pageItems = all.slice((clampedPage - 1) * pageSize, clampedPage * pageSize);
            return (
              <Stack spacing={1}>
                {pageItems.map((e) => (
                  <Stack
                    key={e.id}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ p: '12px 14px', border: `1px solid ${T.border}`, borderRadius: T.radiusSm, bgcolor: T.surfaceAlt }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontFamily: font, fontSize: '0.95rem', fontWeight: 600, color: T.text }} noWrap>
                        {e.title || 'Event'}
                      </Typography>
                      <Typography sx={{ fontFamily: font, fontSize: '0.82rem', color: T.textMuted }}>
                        {e.eventDate ? new Date(e.eventDate).toLocaleString() : '—'}
                      </Typography>
                    </Box>
                    <StatusBadge label={e.status?.toUpperCase() || 'COMPLETED'} color={T.success} />
                  </Stack>
                ))}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 2 }}>
                  <Typography sx={{ ...sxLabel, fontSize: '0.75rem' }}>
                    Page {clampedPage} of {totalPages}
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={clampedPage}
                    onChange={(_, p) => setAttendancePage(p)}
                    size="small"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontFamily: font, color: T.textMuted,
                        border: `1px solid ${T.border}`,
                        borderRadius: T.radiusXs, fontSize: '0.82rem',
                      },
                      '& .Mui-selected': {
                        bgcolor: `${T.accent}18 !important`,
                        color: T.accent,
                        borderColor: alpha(T.accent, 0.3),
                      },
                    }}
                  />
                </Stack>
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px', borderTop: `1px solid ${T.border}` }}>
          <Button
            onClick={() => setAttendanceOpen(false)}
            sx={{
              fontFamily: font, color: T.textMuted, fontSize: '0.88rem',
              textTransform: 'none', border: `1px solid ${T.border}`,
              borderRadius: T.radiusSm, px: 2.5,
              '&:hover': { bgcolor: T.surfaceHover, color: T.text, borderColor: T.borderHover },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => !editSaving && setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontFamily: font, fontWeight: 700 }}>Edit profile</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Display name" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} fullWidth required />
            <TextField label="TMP in-game name" value={editForm.tmpIngameName} onChange={(e) => setEditForm((f) => ({ ...f, tmpIngameName: e.target.value }))} fullWidth />
            <TextField label="Avatar URL" value={editForm.avatar} onChange={(e) => setEditForm((f) => ({ ...f, avatar: e.target.value }))} fullWidth />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Age" type="number" value={editForm.age} onChange={(e) => setEditForm((f) => ({ ...f, age: e.target.value }))} fullWidth />
              <TextField label="Country" value={editForm.country} onChange={(e) => setEditForm((f) => ({ ...f, country: e.target.value }))} fullWidth />
            </Stack>
            <Typography variant="subtitle2" sx={{ fontFamily: font, fontWeight: 700, pt: 1 }}>Connected accounts</Typography>
            <TextField label="Steam ID (64)" value={editForm.steamID} onChange={(e) => setEditForm((f) => ({ ...f, steamID: e.target.value }))} fullWidth helperText="Used for steamcommunity.com/profiles/{id}" />
            <TextField label="TruckersMP user ID" value={editForm.truckersmpId} onChange={(e) => setEditForm((f) => ({ ...f, truckersmpId: e.target.value }))} fullWidth helperText="Opens truckersmp.com/user/{id}" />
            <TextField label="TruckersHub profile ID" value={editForm.truckershubId} onChange={(e) => setEditForm((f) => ({ ...f, truckershubId: e.target.value }))} fullWidth helperText="Opens truckershub.in/profile/{id}" />
            <TextField label="Truckline MP ID (optional)" value={editForm.trucklineMpId} onChange={(e) => setEditForm((f) => ({ ...f, trucklineMpId: e.target.value }))} fullWidth helperText="Public WebID — prefer Link Truckline MP account for verified OAuth" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} disabled={editSaving}>Cancel</Button>
          <Button variant="contained" onClick={saveEditProfile} disabled={editSaving || !editForm.name.trim()}>
            {editSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </MagicPageShell>
  );
}
